import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, History, Settings } from "lucide-react";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/history", label: "History", icon: History },
  { path: "/settings", label: "Settings", icon: Settings },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <aside
      className="fixed left-0 top-0 bottom-0 z-50 w-[220px] flex flex-col py-7 bg-surface border-r border-border transition-all duration-300"
      style={{
        boxShadow: "4px 0 32px rgba(var(--shadow-rgb), calc(var(--shadow-intensity) * 0.45))",
      }}
    >
      {/* Top subtle glow (less intense in light mode) */}
      <div
        className="absolute top-0 left-0 right-0 h-48 pointer-events-none transition-all duration-300 opacity-20 dark:opacity-10"
        style={{
          background: "linear-gradient(180deg, var(--accent) 0%, transparent 100%)",
        }}
      />

      <div className="px-5 pb-8 relative shrink-0">
        <div className="flex flex-col gap-2">
          {/* Unified Seton Brand Logo SVG */}
          <div className="flex items-center">
            <svg
              id="Layer_1"
              data-name="Layer 1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="25 15 310 205"
              className="w-[125px] h-auto text-slate-800 dark:text-white fill-current transition-colors duration-300"
            >
              <g>
                <path d="M144.25,65.95l-28.34-46.99,4.65,43.19c-4.21,4.19-6.73,7.6-9.92,12.64-2.71,4.28-4.96,8.76-6.79,13.37h18.98c5.03-9.12,12.39-16.75,21.42-22.21Z"/>
                <path d="M207.58,169.72c-9.17,5.54-19.92,8.73-31.41,8.73-22.27,0-41.73-11.97-52.32-29.83h-19.01c6.26,14.59,16.74,26.96,30.45,35.63,20.1,12.71,44.68,15.66,67.06,8.35l32.61,22.53-27.38-45.42Z"/>
              </g>
              <g>
                <path d="M251.23,90.93c-4.6-13.59-12.07-25.34-23.33-34.8-16.22-13.63-36.79-20.13-57.9-18.3-5.95.52-9.65,1.04-15.31,2.86l-35.72-23.65,29.15,46.64c8.56-4.6,17.66-6.85,28.06-6.85,23.2,0,43.36,12.99,53.61,32.1,2.51-.63,5.15-.96,7.89-.96,5.19,0,9.29.83,13.56,2.98Z"/>
                <path d="M237.67,148.75c-2.93,0-5.75-.39-8.42-1.1-4.47,7.92-10.71,14.7-18.32,19.89l26.88,45.87-5.3-39.99c7.96-7.84,14.09-17.19,18.12-27.37-3.93,1.75-8.32,2.71-12.97,2.71Z"/>
              </g>
              <g>
                <path d="M38.02,134.54l8.96-5.19c2.36,3.85,5.42,6.68,10.84,6.68,4.56,0,7.46-2.28,7.46-5.42,0-3.77-2.99-5.11-8.01-7.31l-2.75-1.18c-7.94-3.38-13.2-7.62-13.2-16.58,0-8.25,6.29-14.54,16.11-14.54,6.99,0,12.02,2.44,15.63,8.8l-8.56,5.5c-1.89-3.38-3.93-4.71-7.07-4.71s-5.26,2.04-5.26,4.71c0,3.3,2.04,4.64,6.76,6.68l2.75,1.18c9.35,4.01,14.61,8.09,14.61,17.28,0,9.9-7.78,15.32-18.23,15.32s-16.81-4.87-20.03-11.24Z"/>
                <path d="M96.63,91.64h35.36v9.66h-24.36v16.97h21.45v9.43h-21.45v7.7h25.46v9.66h-36.46v-53.43Z"/>
                <path d="M168.84,101.3h-15.24v-9.66h41.48v9.66h-15.24v43.76h-11v-43.76Z"/>
                <path d="M210.02,118.35c0-15.79,12.26-27.42,27.66-27.42s27.66,11.63,27.66,27.42-12.26,27.42-27.66,27.42-27.66-11.63-27.66-27.42ZM254.02,118.35c0-9.98-6.6-17.6-16.34-17.6s-16.34,7.62-16.34,17.6,6.6,17.6,16.34,17.6,16.34-7.62,16.34-17.6Z"/>
                <path d="M302.34,120.24c-2.67-2.51-6.36-7.31-6.36-7.31,0,0,.79,5.89.79,9.74v22.39h-10.29v-53.9h1.1l26.95,25.3c2.59,2.44,6.29,7.31,6.29,7.31,0,0-.71-6.05-.71-9.74v-22.39h10.29v53.9h-1.1l-26.95-25.3Z"/>
              </g>
            </svg>
          </div>
          <div className="text-[10px] text-text-muted uppercase tracking-[0.12em] font-semibold transition-colors duration-300 ml-0.5 mt-0.5">
            Rental Property
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 relative">
        {navItems.map(({ path, label, icon: Icon }) => {
          const active = location.pathname === path;
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14.5px] mb-1.5 transition-all duration-200 ease-out outline-none border ${
                active
                  ? "nav-item-active font-bold border-transparent"
                  : "text-text-secondary hover:text-text-primary hover:bg-surface-2 border-transparent font-medium"
              }`}
            >
              <Icon size={16} className={active ? "text-accent" : "text-text-muted"} />
              {label}
            </button>
          );
        })}
      </nav>

      {/* Property details footer */}
      <div className="pt-4 px-5 text-[11px] text-text-muted border-t border-border transition-colors duration-300 shrink-0">
        <span className="font-semibold text-text-secondary">Property Address:</span>
        <div className="mt-1 font-mono leading-relaxed">
          39 Seton Parade SE
          <br />
          Calgary AB T3M 3C5
        </div>
      </div>
    </aside>
  );
}
